import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ImageReveal, Reveal } from "@/components/Reveal";
import { useProducts } from "@/components/home/ProductRail";
import { ProductCard } from "@/components/ProductCard";

const U = (id, w = 1600) => `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&q=80&w=${w}`;

export function CollectionGrid() {
  const { t } = useStore();
  const [cols, setCols] = useState([]);
  useEffect(() => { api.get("/collections").then((r) => setCols(r.data)).catch(() => {}); }, []);
  const spans = ["lg:col-span-7 lg:row-span-2", "lg:col-span-5", "lg:col-span-5", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4",
    "lg:col-span-3", "lg:col-span-3", "lg:col-span-3", "lg:col-span-3"];
  return (
    <section id="collections" className="py-24 sm:py-32" data-testid="collections-section">
      <div className="container-x">
        <Reveal className="mb-14 flex items-end justify-between">
          <h2 className="font-display text-4xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl">{t("shopByCollection")}</h2>
          <Link to="/collections" className="link-u hidden font-mono text-xs font-semibold uppercase tracking-[0.2em] sm:block" data-testid="collections-view-all">{t("viewAll")}</Link>
        </Reveal>
        <div className="grid auto-rows-[260px] grid-cols-2 gap-3 sm:auto-rows-[320px] lg:grid-cols-12">
          {cols.map((c, i) => (
            <Link key={c.slug} to={`/collections/${c.slug}`} className={`group relative overflow-hidden bg-concrete ${i === 0 ? "col-span-2" : ""} ${spans[i]}`} data-testid={`collection-card-${c.slug}`}>
              <ImageReveal src={c.image} alt={c.title} className="absolute inset-0 transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent transition-opacity duration-500 group-hover:from-ink/90" />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5 text-sun sm:p-7">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sun/60">{c.count} {t("products")}</p>
                  <h3 className={`mt-1 font-display font-black uppercase leading-none tracking-tight ${i === 0 ? "text-5xl sm:text-7xl" : "text-2xl sm:text-3xl"}`}>{c.title}</h3>
                </div>
                <ArrowUpRight className="h-6 w-6 shrink-0 transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function JordanCulture() {
  const { t } = useStore();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["20%", "-20%"]);
  const items = useProducts({ collection: "jordan-retro", sort: "popular", limit: 3 });
  return (
    <section ref={ref} className="grain relative overflow-hidden bg-ink py-24 text-sun sm:py-36" data-testid="jordan-culture-section">
      <div className="container-x relative grid gap-12 lg:grid-cols-12">
        <div className="relative h-[60vh] overflow-hidden lg:col-span-7 lg:h-[85vh]">
          <motion.img style={{ y }} src={U("photo-1618677831708-0e7fda3148b4", 1800)} alt="Air Jordan collection" className="absolute inset-0 h-[124%] w-full object-cover" />
          <div className="absolute inset-0 bg-ink/20" />
          <p className="absolute bottom-6 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-sun/70">Since 1985</p>
        </div>
        <div className="flex flex-col justify-between lg:col-span-5">
          <Reveal>
            <p className="eyebrow mb-6 text-silver">Chapter 02</p>
            <h2 className="font-display text-6xl font-black uppercase leading-[0.85] tracking-tighter sm:text-8xl">{t("jordanCulture")}</h2>
            <p className="mt-8 max-w-md text-lg text-sun/60">{t("jordanCultureSub")}</p>
            <Link to="/collections/air-jordan" className="btn-white mt-10" data-testid="jordan-culture-shop-btn">{t("shopNow")} <ArrowUpRight className="h-4 w-4" /></Link>
          </Reveal>
          <motion.div style={{ y: y2 }} className="mt-14 grid grid-cols-3 gap-3">
            {(items || []).map((p) => (
              <Link key={p.id} to={`/product/${p.slug}`} className="group block" data-testid={`jordan-culture-item-${p.slug}`}>
                <div className="aspect-square overflow-hidden"><img src={p.images[0]} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" /></div>
                <p className="mt-2 line-clamp-1 font-mono text-[10px] uppercase tracking-widest text-sun/60">{p.model}</p>
              </Link>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function NikeEssentials() {
  const { t } = useStore();
  const families = ["Air Force 1", "Dunk Low", "Air Max 90", "Air Max 95", "Air Max 97", "Blazer", "Cortez", "P-6000", "Pegasus", "Vomero", "Shox", "Dunk High", "SB Dunk"];
  const items = useProducts({ collection: "nike", featured: true, limit: 4 });
  return (
    <section className="bg-concrete py-24 sm:py-32" data-testid="nike-essentials-section">
      <div className="container-x">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p className="eyebrow mb-6 text-ink/50">Chapter 03</p>
            <h2 className="font-display text-5xl font-black uppercase leading-[0.88] tracking-tight sm:text-7xl">{t("nikeEssentials")}</h2>
            <p className="mt-6 max-w-sm text-ink/60">{t("nikeEssentialsSub")}</p>
            <div className="mt-10 flex flex-wrap gap-2">
              {families.map((f) => (
                <Link key={f} to={`/collections/nike?model=${encodeURIComponent(f)}`} data-testid={`nike-family-${f.replace(/\s/g, "-").toLowerCase()}`}
                  className="border border-ink/15 bg-sun px-3 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors hover:border-ink hover:bg-ink hover:text-sun">{f}</Link>
              ))}
            </div>
          </Reveal>
          <div className="grid grid-cols-2 gap-5 lg:col-span-8">
            {(items || []).map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
}

export function ExclusiveDrops() {
  const { t } = useStore();
  const items = useProducts({ exclusive: true, sort: "popular", limit: 3 });
  if (items && items.length === 0) return null;
  return (
    <section className="py-24 sm:py-32" data-testid="exclusive-drops-section">
      <div className="container-x">
        <Reveal className="mb-14 grid gap-6 lg:grid-cols-12 lg:items-end">
          <h2 className="font-display text-5xl font-black uppercase leading-[0.88] tracking-tight sm:text-7xl lg:col-span-8">{t("exclusiveDrops")}</h2>
          <p className="text-ink/60 lg:col-span-4">{t("exclusiveSub")}</p>
        </Reveal>
        <div className="grid gap-px bg-concrete md:grid-cols-3">
          {(items || []).map((p, i) => (
            <Link key={p.id} to={`/product/${p.slug}`} className="group relative block bg-sun p-6" data-testid={`exclusive-drop-${p.slug}`}>
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50">
                <span>Drop {String(i + 1).padStart(2, "0")}</span>
                <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-lavender" /> Limited</span>
              </div>
              <div className="my-6 aspect-square overflow-hidden bg-concrete">
                <img src={p.images[0]} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-110 group-hover:rotate-[-2deg]" />
              </div>
              <p className="eyebrow text-ink/50">{p.brand}</p>
              <p className="mt-1 font-display text-xl font-bold uppercase leading-tight">{p.name}</p>
              <ArrowUpRight className="absolute bottom-6 right-6 h-5 w-5 transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function EverydayIcons() {
  const { t } = useStore();
  return (
    <section className="relative" data-testid="everyday-icons-section">
      <div className="grid lg:grid-cols-2">
        <ImageReveal src={U("photo-1595341888016-a392ef81b7de")} alt="Everyday sneakers street style" className="h-[70vh] lg:h-[90vh]" />
        <div className="flex flex-col justify-center bg-sun px-6 py-20 sm:px-12 lg:px-20">
          <Reveal>
            <p className="eyebrow mb-6 text-ink/50">Chapter 04</p>
            <h2 className="font-display text-5xl font-black uppercase leading-[0.88] tracking-tight sm:text-7xl">{t("everydayIcons")}</h2>
            <p className="mt-6 max-w-md text-ink/60">{t("everydaySub")}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/collections/lifestyle" className="btn-ink" data-testid="everyday-lifestyle-btn">Lifestyle</Link>
              <Link to="/collections/running" className="btn-line hover:bg-ink hover:text-sun" data-testid="everyday-running-btn">Running</Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function BrandMarquee() {
  const brands = ["NIKE", "AIR JORDAN", "ADIDAS", "NEW BALANCE", "PUMA", "ASICS", "JORDAN RETRO", "STREETWEAR"];
  return (
    <div className="overflow-hidden border-y border-ink bg-ink py-6" data-testid="brand-marquee">
      <div className="animate-marquee flex w-max gap-16 whitespace-nowrap">
        {[...brands, ...brands].map((b, i) => (
          <Link key={i} to={`/collections/${b.toLowerCase().replace(/\s/g, "-")}`} className="wordmark text-3xl text-sun transition-colors hover:text-lavender sm:text-5xl">
            {b}<span className="ml-16 text-lavender">✕</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
